import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Star, Clock, Calendar, TrendingUp, Users } from 'lucide-react';
import { format } from 'date-fns';

interface DashboardRendererProps {
  dashboardType: string;
  dashboardConfig: Record<string, unknown>;
  businessId: string;
}

export function DashboardRenderer({
  dashboardType,
  dashboardConfig,
  businessId,
}: DashboardRendererProps) {
  switch (dashboardType) {
    case 'reviews':
      return <ReviewsDashboard businessId={businessId} config={dashboardConfig} />;
    case 'schedule':
      return <ScheduleDashboard businessId={businessId} config={dashboardConfig} />;
    case 'stats':
      return <StatsDashboard businessId={businessId} config={dashboardConfig} />;
    case 'welcome':
      return <WelcomeDashboard config={dashboardConfig} />;
    default:
      return (
        <div className="w-full h-full flex items-center justify-center bg-slate-900 text-white">
          <p>Unknown dashboard type: {dashboardType}</p>
        </div>
      );
  }
}

// Reviews Dashboard - Shows recent reviews and average rating
function ReviewsDashboard({
  businessId,
  config,
}: {
  businessId: string;
  config: Record<string, unknown>;
}) {
  const [reviews, setReviews] = useState<any[]>([]);
  const [avgRating, setAvgRating] = useState<number | null>(null);
  const [totalReviews, setTotalReviews] = useState(0);

  useEffect(() => {
    fetchReviews();
    const interval = setInterval(fetchReviews, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [businessId]);

  const fetchReviews = async () => {
    try {
      // Fetch recent reviews
      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('id, rating, comment, customer_name, created_at')
        .eq('business_id', businessId)
        .gte('rating', 4) // Only show positive reviews
        .order('created_at', { ascending: false })
        .limit(5);

      setReviews(reviewsData || []);

      // Fetch stats
      const { data: statsData } = await supabase
        .from('reviews')
        .select('rating')
        .eq('business_id', businessId);

      if (statsData && statsData.length > 0) {
        const avg = statsData.reduce((sum, r) => sum + r.rating, 0) / statsData.length;
        setAvgRating(Math.round(avg * 10) / 10);
        setTotalReviews(statsData.length);
      }
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    }
  };

  const primaryColor = (config?.primaryColor as string) || '#3b82f6';

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8 flex flex-col">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-2">Customer Reviews</h1>
        {avgRating && (
          <div className="flex items-center justify-center gap-3 text-2xl">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-8 h-8 ${
                    star <= Math.round(avgRating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-slate-600'
                  }`}
                />
              ))}
            </div>
            <span className="font-bold">{avgRating}</span>
            <span className="text-slate-400">({totalReviews} reviews)</span>
          </div>
        )}
      </div>

      {/* Reviews Grid */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-hidden">
        {reviews.map((review, index) => (
          <div
            key={review.id}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-6 flex flex-col"
            style={{
              animationDelay: `${index * 0.1}s`,
            }}
          >
            <div className="flex mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-5 h-5 ${
                    star <= review.rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-slate-600'
                  }`}
                />
              ))}
            </div>
            {review.comment && (
              <p className="text-lg flex-1 line-clamp-4 mb-4">"{review.comment}"</p>
            )}
            <div className="text-slate-400 text-sm">
              <p className="font-medium text-white">{review.customer_name || 'Customer'}</p>
              <p>{format(new Date(review.created_at), 'MMM d, yyyy')}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="text-center mt-8 text-slate-400">
        <p>Thank you for your feedback!</p>
      </div>
    </div>
  );
}

// Schedule Dashboard - Shows today's schedule/events
function ScheduleDashboard({
  businessId,
  config,
}: {
  businessId: string;
  config: Record<string, unknown>;
}) {
  const [shifts, setShifts] = useState<any[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchSchedule();
    const interval = setInterval(fetchSchedule, 300000); // Refresh every 5 minutes
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(interval);
      clearInterval(timeInterval);
    };
  }, [businessId]);

  const fetchSchedule = async () => {
    try {
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString();
      const endOfDay = new Date(today.setHours(23, 59, 59, 999)).toISOString();

      const { data } = await supabase
        .from('shifts')
        .select(`
          id,
          start_time,
          end_time,
          employees (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .gte('start_time', startOfDay)
        .lte('start_time', endOfDay)
        .order('start_time');

      setShifts(data || []);
    } catch (err) {
      console.error('Failed to fetch schedule:', err);
    }
  };

  const title = (config?.title as string) || "Today's Schedule";

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold">{title}</h1>
          <p className="text-xl text-slate-400 mt-2">
            {format(currentTime, 'EEEE, MMMM d, yyyy')}
          </p>
        </div>
        <div className="text-right">
          <div className="text-6xl font-light font-mono">
            {format(currentTime, 'HH:mm')}
          </div>
        </div>
      </div>

      {/* Schedule */}
      <div className="flex-1 overflow-hidden">
        {shifts.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-400">
            <div className="text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-xl">No shifts scheduled for today</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {shifts.map((shift) => {
              const startTime = new Date(shift.start_time);
              const endTime = new Date(shift.end_time);
              const isActive =
                currentTime >= startTime && currentTime <= endTime;

              return (
                <div
                  key={shift.id}
                  className={`p-4 rounded-xl ${
                    isActive
                      ? 'bg-green-500/20 border border-green-500'
                      : 'bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        isActive ? 'bg-green-500 animate-pulse' : 'bg-slate-500'
                      }`}
                    />
                    <div>
                      <p className="font-medium text-lg">
                        {shift.employees?.first_name} {shift.employees?.last_name}
                      </p>
                      <p className="text-slate-400">
                        {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Stats Dashboard - Shows business metrics
function StatsDashboard({
  businessId,
  config,
}: {
  businessId: string;
  config: Record<string, unknown>;
}) {
  const [stats, setStats] = useState({
    todayCustomers: 0,
    weekCustomers: 0,
    avgRating: 0,
    totalReviews: 0,
  });

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [businessId]);

  const fetchStats = async () => {
    try {
      // This would fetch actual stats from your database
      // For now, showing placeholder logic
      const { data: reviews } = await supabase
        .from('reviews')
        .select('rating')
        .eq('business_id', businessId);

      if (reviews) {
        const avg =
          reviews.length > 0
            ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
            : 0;
        setStats({
          todayCustomers: Math.floor(Math.random() * 50) + 10,
          weekCustomers: Math.floor(Math.random() * 300) + 50,
          avgRating: Math.round(avg * 10) / 10,
          totalReviews: reviews.length,
        });
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const title = (config?.title as string) || 'Business Metrics';

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8 flex flex-col">
      <h1 className="text-4xl font-bold mb-8 text-center">{title}</h1>

      <div className="flex-1 grid grid-cols-2 gap-8">
        <StatCard
          icon={<Users className="w-12 h-12" />}
          label="Today's Customers"
          value={stats.todayCustomers.toString()}
          color="bg-blue-500/20"
        />
        <StatCard
          icon={<TrendingUp className="w-12 h-12" />}
          label="This Week"
          value={stats.weekCustomers.toString()}
          color="bg-green-500/20"
        />
        <StatCard
          icon={<Star className="w-12 h-12" />}
          label="Average Rating"
          value={stats.avgRating.toFixed(1)}
          color="bg-yellow-500/20"
        />
        <StatCard
          icon={<Star className="w-12 h-12" />}
          label="Total Reviews"
          value={stats.totalReviews.toString()}
          color="bg-purple-500/20"
        />
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className={`${color} rounded-2xl p-8 flex flex-col items-center justify-center`}>
      <div className="text-white/80 mb-4">{icon}</div>
      <div className="text-5xl font-bold mb-2">{value}</div>
      <div className="text-xl text-slate-400">{label}</div>
    </div>
  );
}

// Welcome Dashboard - Customizable welcome message
function WelcomeDashboard({ config }: { config: Record<string, unknown> }) {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const title = (config?.title as string) || 'Welcome';
  const subtitle = (config?.subtitle as string) || '';
  const showClock = config?.showClock !== false;

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col items-center justify-center">
      {showClock && (
        <div className="text-8xl font-light font-mono mb-8">
          {format(currentTime, 'HH:mm')}
        </div>
      )}
      <h1 className="text-6xl font-bold mb-4">{title}</h1>
      {subtitle && <p className="text-2xl text-slate-400">{subtitle}</p>}
      <p className="text-xl text-slate-500 mt-8">
        {format(currentTime, 'EEEE, MMMM d, yyyy')}
      </p>
    </div>
  );
}

export default DashboardRenderer;
